import React, { useEffect, useState } from "react";
import VideoUploadForm from "../components/VideoUploadForm";
import AnnouncementUploadForm from "../components/AnnouncementUploadForm";
import StoryUploadForm from "../components/StoryUploadForm";
import { setPageMeta } from "../seo/setPageMeta";

export default function Admin() {
  const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [videos, setVideos] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [stories, setStories] = useState([]);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [dailyEncouragements, setDailyEncouragements] = useState([]);
  const [encouragementMessage, setEncouragementMessage] = useState("");

  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("success");
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState("");
  const [checkingAuth, setCheckingAuth] = useState(true);

  const [activeTab, setActiveTab] = useState("encouragement");

  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showStoryForm, setShowStoryForm] = useState(false);

  const [editingVideo, setEditingVideo] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [editingStory, setEditingStory] = useState(null);

  const canDelete = adminRole === "superAdmin";

  useEffect(() => {
    setPageMeta({
      title: "Admin | Celebrate Recovery Independence KY",
      description:
        "Administrative dashboard for managing daily encouragement, announcements, videos, stories, and prayer requests.",
      canonical: "https://www.crindependenceky.com/admin",
      noindex: true,
    });
    validateAdminSession();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchVideos();
      fetchAnnouncements();
      fetchStories();
      fetchPrayerRequests();
      fetchDailyEncouragements();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!statusMessage) return;
    const timer = setTimeout(() => setStatusMessage(""), 4000);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  function showStatus(message, type = "success") {
    setStatusMessage(message);
    setStatusType(type);
  }

  async function validateAdminSession() {
    try {
      const response = await fetch(`${API_URL}/api/admin/me`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Not authenticated");
      const data = await response.json();
      setIsAdmin(true);
      setAdminRole(data.role || "admin");
    } catch {
      setIsAdmin(false);
      setAdminRole("");
    } finally {
      setCheckingAuth(false);
    }
  }

  async function fetchVideos() {
    try {
      const response = await fetch(`${API_URL}/api/videos`, { credentials: "include" });
      const data = await response.json();
      setVideos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch videos:", error);
      showStatus("Failed to load videos.", "error");
    }
  }

  async function fetchAnnouncements() {
    try {
      const response = await fetch(`${API_URL}/api/admin/announcements`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch announcements");
      const data = await response.json();
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
      showStatus("Failed to load announcements.", "error");
    }
  }

  async function fetchStories() {
    try {
      const response = await fetch(`${API_URL}/api/admin/stories`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch stories");
      const data = await response.json();
      setStories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch stories:", error);
      showStatus("Failed to load stories.", "error");
    }
  }

  async function fetchPrayerRequests() {
    try {
      const response = await fetch(`${API_URL}/api/admin/prayer-requests`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch prayer requests");
      const data = await response.json();
      setPrayerRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch admin prayer requests:", error);
      showStatus("Failed to load prayer requests.", "error");
    }
  }

  async function fetchDailyEncouragements() {
    try {
      const response = await fetch(`${API_URL}/api/admin/daily-encouragement`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch daily encouragement");
      const data = await response.json();
      setDailyEncouragements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch daily encouragement:", error);
      showStatus("Failed to load daily encouragement.", "error");
    }
  }

  function handleCredentialChange(event) {
    const { name, value } = event.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  }

  function closeAllForms() {
    setShowAnnouncementForm(false);
    setShowVideoForm(false);
    setShowStoryForm(false);
    setEditingVideo(null);
    setEditingAnnouncement(null);
    setEditingStory(null);
  }

  function switchTab(tab) {
    setActiveTab(tab);
    closeAllForms();
    setStatusMessage("");
  }

  async function handleLogin(event) {
    event.preventDefault();
    setStatusMessage("");
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username: credentials.username, password: credentials.password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Login failed");
      setCredentials({ username: "", password: "" });
      setIsAdmin(true);
      setAdminRole(data.role || "admin");
      showStatus(
        data.role === "superAdmin" ? "Super admin login successful." : "Admin login successful.",
        "success"
      );
    } catch (error) {
      showStatus(error.message || "Invalid credentials.", "error");
      setIsAdmin(false);
      setAdminRole("");
    }
  }

  async function handleLogout() {
    try {
      await fetch(`${API_URL}/api/admin/logout`, { method: "POST", credentials: "include" });
    } catch (error) {
      console.error("Logout failed:", error);
    }
    setVideos([]);
    setAnnouncements([]);
    setStories([]);
    setPrayerRequests([]);
    setDailyEncouragements([]);
    setEncouragementMessage("");
    setIsAdmin(false);
    setAdminRole("");
    setCheckingAuth(false);
    closeAllForms();
    showStatus("Logged out.", "success");
  }

  function handleStartEditVideo(video) {
    setEditingVideo(video);
    setShowVideoForm(true);
    setShowAnnouncementForm(false);
    setShowStoryForm(false);
    setEditingAnnouncement(null);
    setEditingStory(null);
    setStatusMessage("");
  }

  function handleStartEditAnnouncement(item) {
    setEditingAnnouncement(item);
    setShowAnnouncementForm(true);
    setShowVideoForm(false);
    setShowStoryForm(false);
    setEditingVideo(null);
    setEditingStory(null);
    setStatusMessage("");
  }

  function handleStartEditStory(item) {
    setEditingStory(item);
    setShowStoryForm(true);
    setShowAnnouncementForm(false);
    setShowVideoForm(false);
    setEditingVideo(null);
    setEditingAnnouncement(null);
    setStatusMessage("");
  }

  function handleToggleAnnouncementForm() {
    const opening = !showAnnouncementForm;
    setShowAnnouncementForm(opening);
    setShowVideoForm(false);
    setShowStoryForm(false);
    setEditingVideo(null);
    setEditingAnnouncement(null);
    setEditingStory(null);
  }

  function handleToggleVideoForm() {
    const opening = !showVideoForm;
    setShowVideoForm(opening);
    setShowAnnouncementForm(false);
    setShowStoryForm(false);
    setEditingAnnouncement(null);
    setEditingStory(null);
    if (!opening) setEditingVideo(null);
  }

  function handleToggleStoryForm() {
    const opening = !showStoryForm;
    setShowStoryForm(opening);
    setShowAnnouncementForm(false);
    setShowVideoForm(false);
    setEditingVideo(null);
    setEditingAnnouncement(null);
    setEditingStory(null);
  }

  async function handleSaveVideo(formData) {
    try {
      const isEditing = !!editingVideo;
      const response = await fetch(
        isEditing ? `${API_URL}/api/videos/${editingVideo._id}` : `${API_URL}/api/videos`,
        { method: isEditing ? "PATCH" : "POST", credentials: "include", body: formData }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to save video");
      showStatus(isEditing ? "Video updated successfully." : "Video added successfully.", "success");
      setEditingVideo(null);
      setShowVideoForm(false);
      fetchVideos();
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to save video.", "error");
    }
  }

  async function handleDeleteVideo(id) {
    if (!window.confirm("Delete this video? This cannot be undone.")) return;
    try {
      const response = await fetch(`${API_URL}/api/videos/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete video");
      setVideos((prev) => prev.filter((video) => video._id !== id));
      if (editingVideo?._id === id) {
        setEditingVideo(null);
        setShowVideoForm(false);
      }
      showStatus("Video deleted successfully.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to delete video.", "error");
    }
  }

  async function handleSaveAnnouncement(formData) {
    const isEditing = !!editingAnnouncement;
    try {
      const response = await fetch(
        isEditing
          ? `${API_URL}/api/admin/announcements/${editingAnnouncement._id}`
          : `${API_URL}/api/admin/announcements`,
        { method: isEditing ? "PATCH" : "POST", credentials: "include", body: formData }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to save announcement");
      showStatus(
        isEditing ? "Announcement updated successfully." : "Announcement published successfully.",
        "success"
      );
      setEditingAnnouncement(null);
      setShowAnnouncementForm(false);
      fetchAnnouncements();
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to save announcement.", "error");
    }
  }

  async function handleDeleteAnnouncement(id) {
    if (!window.confirm("Delete this announcement? This cannot be undone.")) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/announcements/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete announcement");
      setAnnouncements((prev) => prev.filter((item) => item._id !== id));
      showStatus("Announcement deleted successfully.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to delete announcement.", "error");
    }
  }

  async function handleSaveStory(storyData) {
    const isEditing = !!editingStory;
    try {
      const response = await fetch(
        isEditing
          ? `${API_URL}/api/admin/stories/${editingStory._id}`
          : `${API_URL}/api/admin/stories`,
        {
          method: isEditing ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(storyData),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to save story");
      showStatus(isEditing ? "Story updated successfully." : "Story added successfully.", "success");
      setEditingStory(null);
      setShowStoryForm(false);
      fetchStories();
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to save story.", "error");
    }
  }

  async function handleDeleteStory(id) {
    if (!window.confirm("Delete this story? This cannot be undone.")) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/stories/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete story");
      setStories((prev) => prev.filter((item) => item._id !== id));
      showStatus("Story deleted successfully.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to delete story.", "error");
    }
  }

  async function handleAddDailyEncouragement(event) {
    event.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/admin/daily-encouragement`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: encouragementMessage }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to add daily encouragement");
      setEncouragementMessage("");
      showStatus("Daily encouragement published successfully.", "success");
      fetchDailyEncouragements();
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to publish daily encouragement.", "error");
    }
  }

  async function handleDeleteDailyEncouragement(id) {
    if (!window.confirm("Delete this encouragement message? This cannot be undone.")) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/daily-encouragement/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete daily encouragement");
      setDailyEncouragements((prev) => prev.filter((item) => item._id !== id));
      showStatus("Daily encouragement deleted successfully.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to delete daily encouragement.", "error");
    }
  }

  async function handleApprovePrayerRequest(id) {
    try {
      const response = await fetch(`${API_URL}/api/admin/prayer-requests/${id}/approve`, {
        method: "PATCH",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to approve prayer request");
      fetchPrayerRequests();
      showStatus("Prayer request approved.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to approve prayer request.", "error");
    }
  }

  async function handleUnapprovePrayerRequest(id) {
    try {
      const response = await fetch(`${API_URL}/api/admin/prayer-requests/${id}/unapprove`, {
        method: "PATCH",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to unapprove prayer request");
      fetchPrayerRequests();
      showStatus("Prayer request hidden from public view.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to unapprove prayer request.", "error");
    }
  }

  async function handleDeletePrayerRequest(id) {
    if (!window.confirm("Delete this prayer request? This cannot be undone.")) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/prayer-requests/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete prayer request");
      setPrayerRequests((prev) => prev.filter((item) => item._id !== id));
      showStatus("Prayer request deleted.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Failed to delete prayer request.", "error");
    }
  }

  function getVideoSourceLabel(video) {
    if (video.videoType === "youtube_live") return "YouTube Live";
    if (video.videoType === "youtube") return "YouTube";
    return "Upload";
  }

  function getRoleLabel(role) {
    return role === "superAdmin" ? "Super Admin" : "Admin";
  }

  if (checkingAuth) {
    return (
      <section>
        <h2 className="page-title">Admin</h2>
        <p className="page-intro">Checking admin access...</p>
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <section>
        <h2 className="page-title">Admin Login</h2>
        <p className="page-intro">
          Authorized ministry administrators can manage daily encouragement,
          announcements, videos, stories, and prayer requests here.
        </p>

        <div className="form-card">
          <form className="form-grid" onSubmit={handleLogin}>
            <div>
              <label className="label" htmlFor="admin-username">Username</label>
              <input
                className="input"
                id="admin-username"
                type="text"
                name="username"
                value={credentials.username}
                onChange={handleCredentialChange}
                required
              />
            </div>

            <div>
              <label className="label" htmlFor="admin-password">Password</label>
              <input
                className="input"
                id="admin-password"
                type="password"
                name="password"
                value={credentials.password}
                onChange={handleCredentialChange}
                required
              />
            </div>

            <button className="btn btn-primary" type="submit">
              Log In
            </button>
          </form>

          {statusMessage && (
            <p className="status-message status-error">{statusMessage}</p>
          )}
        </div>
      </section>
    );
  }

  const tabs = [
    { id: "encouragement", label: "Encouragement" },
    { id: "announcements", label: "Announcements" },
    { id: "videos", label: "Videos" },
    { id: "stories", label: "Stories" },
    { id: "prayers", label: "Prayers" },
  ];

  return (
    <section>
      <div className="admin-header-row">
        <div>
          <h2 className="page-title">Admin Dashboard</h2>
          <p className="video-meta">
            Logged in as: <strong>{getRoleLabel(adminRole)}</strong>
          </p>
          {!canDelete && (
            <p className="video-meta">
              Delete actions are restricted to the super admin account.
            </p>
          )}
        </div>

        <button className="btn btn-secondary" onClick={handleLogout}>
          Log Out
        </button>
      </div>

      {statusMessage && (
        <p className={`status-message status-${statusType}`}>{statusMessage}</p>
      )}

      <div className="admin-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`admin-tab-btn${activeTab === tab.id ? " active" : ""}`}
            onClick={() => switchTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="admin-tab-content">

        {activeTab === "encouragement" && (
          <div className="card daily-encouragement-admin-card">
            <h3>Daily Encouragement</h3>
            <p className="video-meta">
              Add a one-line encouragement message for the homepage.
            </p>

            <form onSubmit={handleAddDailyEncouragement} className="form-grid">
              <div>
                <label className="label" htmlFor="encouragement-input">
                  Encouragement Message
                </label>
                <div className="daily-encouragement-input-wrap">
                  <input
                    className="input daily-encouragement-input"
                    id="encouragement-input"
                    type="text"
                    value={encouragementMessage}
                    onChange={(event) => setEncouragementMessage(event.target.value)}
                    placeholder="God is still working, one day at a time."
                    maxLength={240}
                    required
                  />
                  <span className="daily-encouragement-counter">
                    {encouragementMessage.length}/240
                  </span>
                </div>
              </div>

              <button className="btn btn-primary" type="submit">
                Publish Encouragement
              </button>
            </form>

            <div className="section-spacing">
              <h4>Current Encouragements</h4>

              {dailyEncouragements.length === 0 ? (
                <div className="daily-encouragement-empty">
                  No daily encouragement messages published yet.
                </div>
              ) : (
                <div className="daily-encouragement-list">
                  {dailyEncouragements.map((item) => (
                    <div key={item._id} className="daily-encouragement-item">
                      <p className="daily-encouragement-item-message">
                        "{item.message}"
                      </p>
                      <p className="daily-encouragement-item-meta">
                        {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
                      </p>
                      {canDelete && (
                        <div className="button-row">
                          <button
                            className="btn delete-btn"
                            type="button"
                            onClick={() => handleDeleteDailyEncouragement(item._id)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "announcements" && (
          <div>
            <div className="admin-action-bar">
              <button
                type="button"
                className={`btn ${showAnnouncementForm ? "btn-secondary" : "btn-primary"}`}
                onClick={handleToggleAnnouncementForm}
              >
                {showAnnouncementForm
                  ? editingAnnouncement
                    ? "Close Announcement Editor"
                    : "Close Announcement Form"
                  : "New Announcement"}
              </button>
            </div>

            {showAnnouncementForm && (
              <div className="section-spacing">
                <AnnouncementUploadForm
                  onSaveAnnouncement={handleSaveAnnouncement}
                  isAdmin={true}
                  editingAnnouncement={editingAnnouncement}
                />
              </div>
            )}

            <div className="card section-spacing">
              <h3>Current Announcements</h3>

              {announcements.length === 0 ? (
                <p>No announcements published yet.</p>
              ) : (
                <div className="video-list">
                  {announcements.map((item) => (
                    <div key={item._id} className="video-item">
                      <h4>{item.title}</h4>
                      <p className="video-meta">
                        {item.date
                          ? new Date(item.date + "T00:00:00").toLocaleDateString()
                          : ""}
                      </p>
                      <p>{item.message}</p>

                      <div className="button-row">
                        {item.flyerPath && (
                          <a
                            href={`${API_URL}${item.flyerPath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary"
                          >
                            View Flyer
                          </a>
                        )}

                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() => handleStartEditAnnouncement(item)}
                        >
                          Edit
                        </button>

                        {canDelete && (
                          <button
                            className="btn delete-btn"
                            type="button"
                            onClick={() => handleDeleteAnnouncement(item._id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "videos" && (
          <div>
            <div className="admin-action-bar">
              <button
                type="button"
                className={`btn ${showVideoForm ? "btn-secondary" : "btn-primary"}`}
                onClick={handleToggleVideoForm}
              >
                {showVideoForm
                  ? editingVideo
                    ? "Close Video Editor"
                    : "Close Video Upload"
                  : "Upload Video"}
              </button>
            </div>

            {showVideoForm && (
              <div className="section-spacing">
                <VideoUploadForm
                  onAddVideo={handleSaveVideo}
                  isAdmin={true}
                  editingVideo={editingVideo}
                />
              </div>
            )}

            <div className="card section-spacing">
              <h3>Current Videos</h3>

              {videos.length === 0 ? (
                <p>No videos uploaded yet.</p>
              ) : (
                <div className="video-list">
                  {videos.map((video) => (
                    <div key={video._id} className="video-item">
                      <h4>
                        {video.title}{" "}
                        {video.videoType === "youtube_live" && (
                          <span className="admin-badge approved">LIVE</span>
                        )}
                      </h4>
                      <p className="video-meta">Speaker: {video.speaker}</p>
                      <p className="video-meta">
                        Date:{" "}
                        {video.date
                          ? new Date(video.date + "T00:00:00").toLocaleDateString()
                          : ""}
                      </p>
                      <p className="video-meta">Source: {getVideoSourceLabel(video)}</p>
                      {video.description && <p>{video.description}</p>}

                      <div className="button-row">
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() => handleStartEditVideo(video)}
                        >
                          Edit
                        </button>

                        {canDelete && (
                          <button
                            className="btn delete-btn"
                            type="button"
                            onClick={() => handleDeleteVideo(video._id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "stories" && (
          <div>
            <div className="admin-action-bar">
              <button
                type="button"
                className={`btn ${showStoryForm ? "btn-secondary" : "btn-primary"}`}
                onClick={handleToggleStoryForm}
              >
                {showStoryForm
                  ? editingStory
                    ? "Close Story Editor"
                    : "Close Story Form"
                  : "New Story"}
              </button>
            </div>

            {showStoryForm && (
              <div className="section-spacing">
                <StoryUploadForm
                  onSaveStory={handleSaveStory}
                  isAdmin={true}
                  editingStory={editingStory}
                />
              </div>
            )}

            <div className="card section-spacing">
              <h3>Current Stories</h3>

              {stories.length === 0 ? (
                <p>No stories added yet.</p>
              ) : (
                <div className="video-list">
                  {stories.map((item) => (
                    <div key={item._id} className="video-item">
                      <h4>{item.title}</h4>
                      <p className="video-meta">
                        {item.anonymous ? "Anonymous" : item.name}
                      </p>
                      <p className="video-meta">
                        {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
                      </p>
                      <p>{item.story}</p>

                      <div className="button-row">
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() => handleStartEditStory(item)}
                        >
                          Edit
                        </button>

                        {canDelete && (
                          <button
                            className="btn delete-btn"
                            type="button"
                            onClick={() => handleDeleteStory(item._id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "prayers" && (
          <div className="card">
            <h3>Prayer Request Moderation</h3>

            {prayerRequests.length === 0 ? (
              <p>No prayer requests found.</p>
            ) : (
              <div className="video-list">
                {prayerRequests.map((item) => (
                  <div key={item._id} className="video-item">
                    <div className="admin-request-top">
                      <h4>{item.name}</h4>
                      <span
                        className={
                          item.approved ? "admin-badge approved" : "admin-badge pending"
                        }
                      >
                        {item.approved ? "Approved" : "Pending"}
                      </span>
                    </div>

                    <p className="video-meta">
                      {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
                    </p>

                    {!item.anonymous && item.email && (
                      <p className="video-meta">Email: {item.email}</p>
                    )}

                    <p>{item.request}</p>

                    <div className="button-row">
                      {item.approved ? (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() => handleUnapprovePrayerRequest(item._id)}
                        >
                          Unapprove
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary"
                          type="button"
                          onClick={() => handleApprovePrayerRequest(item._id)}
                        >
                          Approve
                        </button>
                      )}

                      {canDelete && (
                        <button
                          className="btn delete-btn"
                          type="button"
                          onClick={() => handleDeletePrayerRequest(item._id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </section>
  );
}
